import React, { FunctionComponent, useRef, useMemo, useCallback } from "react";
import styled from "styled-components";

interface SVGStyleProps extends React.CSSProperties {
  "--clippedPath"?: string;
}

interface AudiowaveProps {
  className?: string;
  rectsItems: {
    id: number;
    height: number;
  }[];
  onClick?: () => void;
}

const Audiowave: FunctionComponent<AudiowaveProps> = ({
  className,
  rectsItems,
  onClick,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const svgHeight = 35;

  const [currentPosition, setCurrentPosition] = React.useState<number>(0);
  const [xPosition, setXPosition] = React.useState(currentPosition);

  const mouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const { clientX } = e;
    const { width, left } = ref.current.getBoundingClientRect();
    const x = clientX - (left + width / 2);
    const maskWidth = width / 2 + x;
    setXPosition(maskWidth);
  }, []);

  const touchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const { clientX } = e.touches[0];
    const { width, left } = ref.current.getBoundingClientRect();
    const x = clientX - (left + width / 2);
    const maskWidth = width / 2 + x;
    setXPosition(maskWidth);
  }, []);

  const mouseLeave = useCallback(() => {
    setXPosition(currentPosition);
  }, [currentPosition]);

  const getWidthPercentage = useCallback((width: number) => {
    if (!ref.current) return;
    const value: number =
      (width * 100) / ref.current?.getBoundingClientRect().width;
    return (100 - value).toFixed();
  }, []);

  const SVGStyle: SVGStyleProps = useMemo(
    () => ({
      "--clippedPath": xPosition ? `${getWidthPercentage(xPosition)}%` : "100%",
    }),
    [getWidthPercentage, xPosition]
  );

  const memoizedRects = useMemo(
    () =>
      rectsItems.map((rect) => {
        const y = (svgHeight - rect.height) / 2;
        return { ...rect, y };
      }),
    [rectsItems]
  );

  const handleClick = useCallback(() => {
    setCurrentPosition(xPosition);
    if (onClick) onClick();
  }, [xPosition, onClick]);

  return (
    <div
      className={className || ""}
      ref={ref}
      onMouseMove={mouseMove}
      onMouseLeave={mouseLeave}
      onTouchMove={touchMove}
      onTouchCancel={mouseLeave}
      onClick={handleClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 604 35"
        height={svgHeight}
        width="100%"
      >
        <g>
          {memoizedRects.map((rect, i) => (
            <rect
              className="audiowave-rect"
              key={i}
              x={i * 8}
              y={rect.y}
              width={4}
              height={rect.height}
              ry={2}
              rx={2}
            />
          ))}
        </g>
      </svg>
      <svg
        className="svg-mask"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 604 35"
        height={svgHeight}
        width="100%"
        style={SVGStyle}
      >
        <g>
          {memoizedRects.map((rect, i) => (
            <rect
              className="audiowave-rect masked"
              key={i}
              x={i * 8}
              y={rect.y}
              width={4}
              height={rect.height}
              ry={2}
              rx={2}
            />
          ))}
        </g>
      </svg>
    </div>
  );
};

export const StyledAudiowave = styled(Audiowave)`
  position: relative;
  .audiowave-rect {
    fill: ${({ theme }) => theme.colors.primary};
    opacity: 0.5;
    stroke-width: 0;
    &.masked {
      opacity: 1;
    }
  }
  .svg-mask {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    --clippedPath: 0;
    clip-path: inset(0 var(--clippedPath) 0 0);
  }
`;
